import {Component, ElementRef, OnDestroy, OnInit} from "@angular/core";
import {FUINavigationService} from "../navigation.service";
import {FUINavigationTree} from "../navigation-trees/navigation-tree/navigation-tree.model";
import {FUINavigationLinkAvailability} from "../navigation-link/navigation-link.model";
import {FormBuilder, FormGroup} from "@angular/forms";
import {NavigationEnd} from "@angular/router";
import {FUINavigationSearchResult} from "./navigation-search-result.model";
import {debounceTime, Observable, ReplaySubject, share, Subject, Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {FUIEventsService} from "../../events/events.service";

@Component({
    animations: [
        trigger("fade", [
            state("void", style({
                opacity: 0
            })),
            state("true", style({
                opacity: 1
            })),
            transition("* => *", animate("450ms 0s ease"))
        ])
    ],
    selector: "fui-navigation-search",
    templateUrl: "./navigation-search.component.html",
    styleUrls: ["./navigation-search.component.scss"],
    standalone: false
})
export class FUINavigationSearchComponent implements OnInit, OnDestroy {
    FUINavigationLinkAvailability = FUINavigationLinkAvailability;

    public form: FormGroup;
    public isActive: boolean;
    public isTyping: boolean;
    private _results: Subject<any> = new Subject<any>();
    public results$: Observable<any> = this._results.asObservable().pipe(share({
        connector: () => new ReplaySubject(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false
    }));

    public results: any[] = [];
    private _query: Subject<string> = new Subject<string>();
    public query$: Observable<string> = this._query.asObservable().pipe(share({
        connector: () => new ReplaySubject(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false
    }));

    public query: string;
    public trees: FUINavigationTree[];

    private subscriptions: Subscription = new Subscription();

    constructor(private elementRef: ElementRef,
                private eventsService: FUIEventsService,
                private navigationService: FUINavigationService,
                private formBuilder: FormBuilder,
                private translateService: TranslateService) {
        this.form = this.formBuilder.group({
            query: ""
        });
    }

    public ngOnInit() {
        /**
         * Get the data to search
         */
        this.subscriptions.add(this.navigationService.getTrees().subscribe((trees) => this.trees = trees));

        /**
         * Reset the state of the search if $navigation occurs
         */
        this.subscriptions.add(this.navigationService.routerEvents$.subscribe(
            (event) => {
                if (event instanceof NavigationEnd) {
                    this.resetActive();
                }
            }
        ));

        this.subscriptions.add(this.eventsService.click$.subscribe(
            (click) => {
                if (this.elementRef.nativeElement.contains(click.target)) {

                } else {
                    this.resetActive();
                }
            }
        ));

        /**
         *  Watch for changes to the results stream
         */
        this.subscriptions.add(this.results$.subscribe(
            (results) => {
                this.results = results;
            }
        ));

        /**
         * Subscribe to changes from the search field. We're debouncing
         * this to allow the user to complete typing before we run a query
         */
        this.subscriptions.add(this.form.get("query").valueChanges.subscribe(
            (query) => {
                if (query) {
                    this.isTyping = true;
                } else {
                    this.isTyping = false;
                }
            }
        ));

        this.subscriptions.add(this.form.get("query").valueChanges.pipe(debounceTime(300)).subscribe(
            (query) => {
                this.query = query;
                this.filter(this.query).then((results) => {
                    this._results.next(results);
                    this.isTyping = false;
                });
            }
        ));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Filter links only, as the links are the
     * only useful part of the search data people can
     * tangibly use and navigate to
     * @param query
     */
    public filter(query: string): Promise<FUINavigationSearchResult[]> {
        const results = [];

        return new Promise<any[]>((resolve, reject) => {
            if (this.trees) {
                if (query) {
                    for (const tree of this.trees) {
                        if (tree.branches) {
                            for (const branch of tree.branches) {
                                if (branch.links) {
                                    for (const link of branch.links) {
                                        if (link.links) {
                                            for (const subLink of link.links) {
                                                this.translateService.get(subLink.title).subscribe((title) => {
                                                    if (title.toLowerCase().includes(query.toLowerCase())) {
                                                        let breadcrumbs: string[] = [tree.title];
                                                        if (branch.title !== tree.title) {
                                                            breadcrumbs.push(branch.title);
                                                        }
                                                        breadcrumbs.push(link.title);
                                                        results.push(new FUINavigationSearchResult({
                                                            breadcrumbs: breadcrumbs,
                                                            href: subLink.href,
                                                            title: subLink.title,
                                                            availability: subLink.availability
                                                        }));
                                                    }
                                                });
                                            }
                                        } else if (link.href) {
                                            /**
                                             * If the links title includes the
                                             * string query then add it to the results
                                             * stream.
                                             * TODO: recursive groups currently only handled to one level deep
                                             */
                                            this.translateService.get(link.title).subscribe((title) => {
                                                if (title.toLowerCase().includes(query.toLowerCase())) {
                                                    let breadcrumbs: string[] = [tree.title];
                                                    if (branch.title !== tree.title) {
                                                        breadcrumbs.push(branch.title);
                                                    }
                                                    results.push(new FUINavigationSearchResult({
                                                        breadcrumbs: breadcrumbs,
                                                        href: link.href,
                                                        title: link.title,
                                                        availability: link.availability
                                                    }));
                                                }
                                            });

                                        }
                                    }
                                }
                            }
                        }
                    }

                    resolve(results);
                }
            } else {
                reject("Navigation data unavailable");
            }
        });
    }

    /**
     * Check the state of the users data
     * entry and focus on the field to determine
     * if they are running a search, leaving a search
     * or not searching at all
     * @returns {boolean}
     */
    public isSearching(): boolean {
        if (!this.isActive && this.query) {
            this._query.next(null);
            this.form.setValue({query: ""})
        }
        return this.isActive && !!this.query && !!this.results;
    }

    /**
     * Reset the active state
     */
    public resetActive(): void {
        this.isActive = false;
    }

    /**
     * Set the active state
     */
    public setActive(): void {
        this.isActive = true;
    }
}
